import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { handlePortalApiError } from '../../api/portal';

const PortalLogin: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isAuthenticated } = useClientAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/portal/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError(t('auth.fillAllFields'));
      return;
    }

    setLoading(true);

    try {
      await login(email, password);
      navigate('/portal/dashboard', { replace: true });
    } catch (err) {
      setError(handlePortalApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('common.clientPortal')}</h1>
          <p className="text-gray-600">Solar Forecast API</p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">{t('auth.login')}</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label={t('auth.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />

            <Input
              label={t('auth.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <Button type="submit" variant="primary" className="w-full" disabled={loading}>
              {loading ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
          </form>

          <div className="mt-6 space-y-3 text-center">
            <div>
              <Link
                to="/portal/forgot-password"
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                {t('auth.forgotPassword')}
              </Link>
            </div>
            <div>
              <Link to="/portal/register" className="text-sm text-blue-600 hover:text-blue-700">
                {t('auth.noAccountRegister')}
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/admin/login" className="text-sm text-gray-600 hover:text-gray-700">
            {t('auth.adminLink')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PortalLogin;
